import * as stylex from "@stylexjs/stylex";
import { AnimatePresence, motion } from "framer-motion";
import useLocale from "hooks/useLocale";
import { ArrowTop16 } from "icons/ArrowTop";
import { locales } from "locales";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useRTL } from "../hooks";
import { useAppReducer } from "../hooks/useAppState";
import * as M from "../materials";
import { colors, zIndex } from "../materials/consts.stylex";
import { gridCenteredMaxWidthAreas, layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { E, O, pipe } from "../prelude";
import { findArticleAlias, RAlias, subthemesIndex, themeAliases } from "../routes";
import { expandHeightMotionProps, getLabel, getThemeLabel } from "./article-switcher";
import { LocaleLink } from "./links";
import { LogoIO } from "./logo";
import { Search } from "./search/search";
import SecondaryBrandsBanner from "./secondary-brands-banner";
const stylexStyles = {
    root: {
        kWkggS: "xdutcdc",
        kMwMTN: "x1vc0dw",
        kmVPX3: "x1tamke2",
        kVAEAm: "x1n2onr6",
        kY2c9j: "xmmwo5t",
        $$css: true
    },
    link: {
        kVAEAm: "x1n2onr6",
        kmVPX3: "x1ib1h6n",
        kMwMTN: "x1heor9g",
        kP9fke: "xtvhhri",
        k63SB2: "x117nqv4",
        kSiTet: "x1us6l5c",
        $$css: true
    },
    linkActive: {
        kSiTet: "x1hc1fzr",
        $$css: true
    },
    articleLink: {
        k1xSpc: "x78zum5",
        kGNEyG: "x6s0dn4",
        kP9fke: "x6mezaz",
        $$css: true
    },
    articleLabel: {
        kmVPX3: "x1xk1jr8",
        $$css: true
    },
    subArticleLabel: {
        k63SB2: "x1fcty0u",
        koQZXg: "x2lknd0",
        kmVPX3: "x1xk1jr8",
        $$css: true
    }
};
export function HeaderSmall() {
    const [{ themeDrawer }, dispatch] = useAppReducer();
    const [isLocaleDrawerOpen, setIsLocaleDrawerOpen] = useState(false);
    const locale = useLocale();
    const { pathname, query: { alias: routeAlias } } = useRouter();
    const handleLocaleDrawerAction = (action)=>{
        if (themeDrawer.isOpen) {
            dispatch({
                type: "themeDrawer.close"
            });
        }
        switch(action){
            case "toggle":
                return setIsLocaleDrawerOpen(!isLocaleDrawerOpen);
            case "close":
                return setIsLocaleDrawerOpen(false);
        }
    };
    const handleThemeDrawerAction = (action)=>{
        if (isLocaleDrawerOpen) {
            setIsLocaleDrawerOpen(false);
        }
        switch(action){
            case "toggle":
                return dispatch({
                    type: "themeDrawer.toggle"
                });
            case "close":
                return dispatch({
                    type: "themeDrawer.close"
                });
        }
    };
    const handleKeyDown = (ev)=>{
        if (ev.keyCode == 13) {
            handleLocaleDrawerAction("close");
            handleThemeDrawerAction("close");
        }
    };
    const drawerMotionVariants = {
        open: {
            height: "auto"
        },
        closed: {
            height: 0
        }
    };
    const alias = pipe(routeAlias, RAlias.decode, E.fold(()=>pathname.startsWith("/focus/") ? "focusPosts" : "home", (alias)=>alias));
    return <>
      <div {...stylex.props(layout.gridCenteredMaxWidth, stylexStyles.root)}>
        <div className="x1yedbjw x78zum5 xdt5ytf x6s0dn4 x1qughib">
          <div className="x78zum5 xh8yej3 x1qughib">
            <Link href="/" className="x1lliihq">
              <LogoIO size="extrasmall"/>
            </Link>

            <LocaleMenuToggle locale={locale} toggle={()=>handleLocaleDrawerAction("toggle")} open={isLocaleDrawerOpen}/>
            <MenuToggle toggle={()=>handleThemeDrawerAction("toggle")} open={themeDrawer.isOpen}></MenuToggle>
          </div>

          <AnimatePresence initial={false} key="localeDrawer">
            {isLocaleDrawerOpen && <motion.div className="xdutcdc x10l6tqk xb3r6kr xh8yej3 xu96u03 x1imwcls xi78jkt" initial="closed" animate="open" exit="closed" variants={drawerMotionVariants}>
                <nav className="xggk2y7 x78zum5 xdt5ytf">
                  {locales.map(({ name, lang }, i)=><span key={lang} {...stylex.props(typography.body1, stylexStyles.link, stylexStyles.linkActive)} onClick={()=>handleLocaleDrawerAction("close")} onKeyDown={handleKeyDown} role="button" // We want to only select LocaleLink with a tab
        tabIndex={-1}>
                      <LocaleLink locale={lang} isFirst={i === 0}>
                        {name}
                      </LocaleLink>
                    </span>)}
                </nav>
              </motion.div>}
          </AnimatePresence>

          <AnimatePresence initial={false} key="themeDrawer">
            {themeDrawer.isOpen && <motion.div className="xdutcdc x10l6tqk xb3r6kr xh8yej3 xu96u03 x1imwcls xi78jkt" initial="closed" animate="open" exit="closed" variants={drawerMotionVariants}>
                <nav className="xggk2y7 x78zum5 xdt5ytf">
                  <Link href="/" {...stylex.props(typography.body1, stylexStyles.link, pathname === "/" && stylexStyles.linkActive)} onClick={()=>handleThemeDrawerAction("close")} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
                    <FormattedMessage id="Home" defaultMessage="Home"/>
                  </Link>

                  <hr className="x14kudrl xc342km xng3xce xbyyjgo xso031l x1q0q8m5 x1ilr58l"/>

                  <div {...stylex.props(typography.body1, stylexStyles.link, O.isSome(findArticleAlias(alias)) && stylexStyles.linkActive)}>
                    <FormattedMessage id="Themes" defaultMessage="Themes"/>
                  </div>

                  {themeAliases.map((d)=>{
        return <ThemeAccordion key={d} alias={d} onClick={()=>handleThemeDrawerAction("close")} onKeyDown={handleKeyDown}/>;
    })}

                  <hr className="x14kudrl xc342km xng3xce xbyyjgo xso031l x1q0q8m5 x1ilr58l"/>

                  <Link href="/focus-articles" {...stylex.props(typography.body1, stylexStyles.link, alias === "focusPosts" && stylexStyles.linkActive)} onClick={()=>handleThemeDrawerAction("close")} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
                    <FormattedMessage id="Focuses" defaultMessage="Focuses"/>
                  </Link>
                  <hr className="x14kudrl xc342km xng3xce xbyyjgo xso031l x1q0q8m5 x1ilr58l"/>

                  <Link href="/indicators" {...stylex.props(typography.body1, stylexStyles.link, alias === "indicators" && stylexStyles.linkActive)} onClick={()=>handleThemeDrawerAction("close")} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
                    <FormattedMessage id="SDG 4 indicators" defaultMessage="SDG 4 indicators"/>
                  </Link>

                  <hr className="x14kudrl xc342km xng3xce xbyyjgo xso031l x1q0q8m5 x1ilr58l"/>

                  <Link href="/about" {...stylex.props(typography.body1, stylexStyles.link, alias === "about" && stylexStyles.linkActive)} onClick={()=>handleThemeDrawerAction("close")} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
                    <FormattedMessage id="About" defaultMessage="About"/>
                  </Link>

                  <hr className="x14kudrl xc342km xng3xce xbyyjgo xso031l x1q0q8m5 x1ilr58l"/>

                  <Search locale={locale}/>
                </nav>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
      <div className="x1hdmf5y">
        {pathname !== "/" ? <SecondaryBrandsBanner overContent={true}/> : null}
      </div>
    </>;
}
const ThemeAccordion = ({ alias, onClick, onKeyDown })=>{
    const [open, setOpen] = React.useState(false);
    const subthemes = subthemesIndex[alias];
    const hasSubthemes = subthemes && subthemes.length > 1;
    const handleClickOrKeyDown = ()=>setOpen(!open);
    return hasSubthemes ? <>
      <div className="x78zum5 x6s0dn4 x1qughib" role="button" tabIndex={0} onClick={handleClickOrKeyDown} onKeyDown={handleClickOrKeyDown}>
        <ArticleLink alias={alias} label={getThemeLabel(alias)} isSubtheme={false}/>
        <div {...{
        0: {
            className: "x1joubo7 x19jd1h0"
        },
        1: {
            className: "x1joubo7 x7p49u4"
        }
    }[!!open << 0]}>
          <ArrowTop16/>
        </div>
      </div>

      {open && <motion.div {...expandHeightMotionProps}>
          {subthemes.map((d)=><Link key={d} href={`/articles/${d}`} {...stylex.props(typography.body1, stylexStyles.link, stylexStyles.articleLink, stylexStyles.linkActive)} role="button" tabIndex={0} onClick={onClick} onKeyDown={onKeyDown}>
              <ArticleLinkContent alias={d} label={getLabel(d)} isSubtheme/>
            </Link>)}
        </motion.div>}
    </> : <Link href={`/articles/${alias}`} {...stylex.props(typography.body1, stylexStyles.link, stylexStyles.articleLink, stylexStyles.linkActive)} role="button" tabIndex={0} onClick={onClick} onKeyDown={onKeyDown}>
      <ArticleLinkContent alias={alias} label={getThemeLabel(alias)} isSubtheme={false}/>
    </Link>;
};
const ArticleLinkContent = ({ alias, label, isSubtheme })=>{
    const theme = M.themes[alias];
    return <>
      {!isSubtheme && theme.kind === "article" && <div className="x1useyqa x900493">
          <img className="xh8yej3 x1lliihq" src={theme.illustration} alt=""/>
        </div>}
      <div {...stylex.props(isSubtheme && typography.body2, isSubtheme ? stylexStyles.subArticleLabel : stylexStyles.articleLabel)}>
        {label}
      </div>
    </>;
};
const ArticleLink = ({ alias, label, isSubtheme })=>{
    return <span {...stylex.props(typography.body1, stylexStyles.link, stylexStyles.articleLink, stylexStyles.linkActive)}>
      <ArticleLinkContent alias={alias} label={label} isSubtheme={isSubtheme}/>
    </span>;
};
const MENU_TOGGLE_MOTION_VARIANTS = [
    {
        closed: {
            d: "M 0 6 L 24 6"
        },
        open: {
            d: "M 2 22 L 22 2"
        }
    },
    {
        closed: {
            d: "M 0 12 L 24 12"
        },
        open: {
            d: "M 12 12 L 12 12"
        }
    },
    {
        closed: {
            d: "M 0 18 L 24 18"
        },
        open: {
            d: "M 2 2 L 22 22"
        }
    }
];
const MenuToggle = ({ toggle, open })=>{
    return <button onClick={toggle} className="xjbqb8w xc342km x1uvtmcs">
      <svg width="24" height="24" viewBox="0 0 24 24">
        {MENU_TOGGLE_MOTION_VARIANTS.map((d, i)=><motion.path key={i} initial={false} fill="transparent" strokeWidth={2} stroke="white" animate={open ? "open" : "closed"} variants={d}/>)}
      </svg>
    </button>;
};
const localeMenuToggleStyles = {
    locale: {
        kVAEAm: "x1n2onr6",
        kmVPX3: "x1ib1h6n",
        kMwMTN: "x1heor9g",
        kP9fke: "xtvhhri",
        kSiTet: "x1us6l5c",
        kmkexE: "xetppgn",
        $$css: true
    },
    localeActive: {
        kSiTet: "x1hc1fzr",
        $$css: true
    }
};
const LocaleMenuToggle = ({ locale, toggle, open })=>{
    const isRTL = useRTL();
    return <button onClick={toggle} {...{
        0: {
            className: "x78zum5 xl56j7k x6s0dn4 x1xq1gxn xjbqb8w xc342km xng3xce x1awj2ng x1uvtmcs x8x9d4c x1yf7rl7"
        },
        1: {
            className: "x78zum5 xl56j7k x6s0dn4 x1xq1gxn xjbqb8w xc342km xng3xce x1awj2ng x1uvtmcs xj3b58b xack27t"
        }
    }[!!isRTL << 0]}>
      <span {...stylex.props(typography.body1, localeMenuToggleStyles.locale, open && localeMenuToggleStyles.localeActive)}>
        {locale}
      </span>
    </button>;
};
